import { createClient, SupabaseClient } from '@supabase/supabase-js'

/**
 * Promo Codes Service
 * Handles all CRUD operations for promo codes
 */

// Create a server-side client for server operations
function getServerClient(): SupabaseClient {
  const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL
  const serviceRoleKey = process.env.SUPABASE_SERVICE_KEY

  if (!supabaseUrl || !serviceRoleKey) {
    throw new Error('Missing Supabase environment variables')
  }

  return createClient(supabaseUrl, serviceRoleKey, {
    auth: {
      autoRefreshToken: false,
      persistSession: false
    }
  })
}

export type PromoDiscountType = 'FREE_SESSIONS' | 'PERCENTAGE' | 'FIXED_AMOUNT'

export interface PromoCode {
  code: string
  description: string
  discountType: PromoDiscountType
  discountValue: number
  validFrom: string
  validUntil?: string | null
  tier?: string | null
  isActive: boolean
  isBetweenSession: boolean
  createdAt: string
  updatedAt: string
}

export interface CreatePromoCodeInput {
  code: string
  description: string
  discountType: PromoDiscountType
  discountValue: number
  validFrom: string
  validUntil?: string | null
  tier?: string | null
  isActive: boolean
  isBetweenSession?: boolean
}

export interface UpdatePromoCodeInput {
  description?: string
  discountType?: PromoDiscountType
  discountValue?: number
  validUntil?: string | null
  tier?: string | null
  isActive?: boolean
  isBetweenSession?: boolean
}

export const promoCodesService = {
  /**
   * Get all promo codes
   */
  async getAll(client?: SupabaseClient): Promise<PromoCode[]> {
    const supabase = client || getServerClient()

    try {
      const { data, error } = await supabase
        .from('promos')
        .select('*')
        .order('createdAt', { ascending: false })

      if (error) {
        console.error('Error fetching promo codes:', error)
        throw error
      }

      return data || []
    } catch (err) {
      console.error('Error in getAll:', err)
      throw err
    }
  },

  /**
   * Create a new promo code
   */
  async create(input: CreatePromoCodeInput, client?: SupabaseClient): Promise<PromoCode> {
    const supabase = client || getServerClient()

    try {
      const now = new Date().toISOString()

      const { data, error } = await supabase
        .from('promos')
        .insert({
          code: input.code.toUpperCase(),
          description: input.description,
          discountType: input.discountType,
          discountValue: input.discountValue,
          validFrom: input.validFrom,
          validUntil: input.validUntil || null,
          tier: input.tier ? input.tier.toUpperCase() : null,
          isActive: input.isActive,
          isBetweenSession: input.isBetweenSession ?? false,
          createdAt: now,
          updatedAt: now
        })
        .select()
        .single()

      if (error) {
        console.error('Error creating promo code:', error)
        throw error
      }

      return data
    } catch (err) {
      console.error('Error in create:', err)
      throw err
    }
  },

  /**
   * Update a promo code
   */
  async update(code: string, input: UpdatePromoCodeInput, client?: SupabaseClient): Promise<PromoCode> {
    const supabase = client || getServerClient()

    try {
      const updateData: any = {
        ...input,
        updatedAt: new Date().toISOString()
      }

      if (input.tier) {
        updateData.tier = input.tier.toUpperCase()
      }

      const { data, error } = await supabase
        .from('promos')
        .update(updateData)
        .eq('code', code)
        .select()
        .single()

      if (error) {
        console.error('Error updating promo code:', error)
        throw error
      }

      return data
    } catch (err) {
      console.error('Error in update:', err)
      throw err
    }
  },

  /**
   * Delete a promo code
   */
  async delete(code: string, client?: SupabaseClient): Promise<void> {
    const supabase = client || getServerClient()

    try {
      const { error } = await supabase
        .from('promos')
        .delete()
        .eq('code', code)

      if (error) {
        console.error('Error deleting promo code:', error)
        throw error
      }
    } catch (err) {
      console.error('Error in delete:', err)
      throw err
    }
  },

  /**
   * Toggle promo code active status
   */
  async toggleActive(code: string, isActive: boolean, client?: SupabaseClient): Promise<PromoCode> {
    const supabase = client || getServerClient()

    try {
      const { data, error } = await supabase
        .from('promos')
        .update({
          isActive: !isActive,
          updatedAt: new Date().toISOString()
        })
        .eq('code', code)
        .select()
        .single()

      if (error) {
        console.error('Error toggling promo status:', error)
        throw error
      }

      return data
    } catch (err) {
      console.error('Error in toggleActive:', err)
      throw err
    }
  }
}
